// @ts-nocheck
import React, { useId, useMemo, useState } from 'react';
import { AlertTriangle, Award, CheckCircle, ChevronDown, ChevronRight } from 'lucide-react';

import { Card, InfoBanner } from '../../components/WorkflowFormPrimitives';
import { fmt } from '../../utils/formatCurrency';
import { calculateEquityAwardQa24cBreakdown } from '../../utils/qa24cEquity';
import { formatIsoYmdForDisplay } from '../../../../utils/isoDateDisplay';

const fmtCents = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const pct = (v, d = 2) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0%';
  return `${(n * 100).toFixed(d)}%`;
};

function flattenAwardsFromForm(formData) {
  const individuals = formData?.individuals || [];
  const equityData = formData?.equityData || {};
  const uniqueNames = Array.from(new Set(individuals.map((i) => i.name)));
  const uniqueIndividuals = uniqueNames.map((name) => individuals.find((i) => i.name === name));
  const rows = [];
  for (const ind of uniqueIndividuals) {
    if (!ind?.id) continue;
    const awards = equityData[ind.id]?.awards || [];
    for (const award of awards) {
      rows.push({ holderId: ind.id, holder: ind.name || 'Individual', award });
    }
  }
  return rows;
}

const VESTING_LABELS = {
  'monthly-4yr-1cliff': 'Monthly / 4yr / 1yr cliff',
  'quarterly-4yr-1cliff': 'Quarterly / 4yr / 1yr cliff',
  'annual-4yr': 'Annual / 4yr / no cliff',
  'annual-4yr-1cliff': 'Annual / 4yr / 1yr cliff',
  'monthly-3yr-1cliff': 'Monthly / 3yr / 1yr cliff',
  'quarterly-3yr-1cliff': 'Quarterly / 3yr / 1yr cliff',
  'annual-3yr': 'Annual / 3yr / no cliff',
  'monthly-4yr': 'Monthly / 4yr / no cliff',
  'quarterly-4yr': 'Quarterly / 4yr / no cliff',
  '2yr-cliff': '2yr cliff (100% at 2yr)',
};

function vestingSummary(award) {
  if (award?.vestingSchedule === 'custom') {
    return `${+award.customVestingMonths || 0} months total, ${+award.customCliffMonths || 0}-month cliff (custom)`;
  }
  return VESTING_LABELS[award?.vestingSchedule] || String(award?.vestingSchedule || '—');
}

function eligibleMessage(calc) {
  if (calc.earlyExit === 'no_accel') return 'No payment acceleration for this award (or option is rolled over).';
  if (calc.earlyExit === 'missing_inputs')
    return 'Add grant date, closing date, and share count to see a full walkthrough.';
  if (calc.useFullValue) return 'Full spread is used because “Use full value” is selected on the award.';
  if (!calc.qualifies24c) {
    const parts = [];
    if (calc.grantWithin12Months) parts.push('granted within 12 months before closing');
    if (calc.vestingType === 'performance' && !calc.performanceConditionMet)
      parts.push('performance conditions not treated as met');
    if (calc.fullValue <= 0) parts.push('no accelerated value at closing');
    return `Q&A-24(c) does not apply${parts.length ? ` (${parts.join('; ')})` : ''} — parachute amount equals the full payment value.`;
  }
  if (calc.uses24cDiscount) {
    return 'Q&A-24(c) applies — time-based award, granted more than 12 months before closing, with a positive accelerated amount.';
  }
  return 'Parachute amount follows the standard equity rules for this award.';
}

function StepRow({ label, value, note, highlight }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className={highlight ? 'font-semibold text-gray-800' : 'text-gray-500'}>{label}</span>
      <div className="text-right">
        <span className={`tabular-nums ${highlight ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
          {value}
        </span>
        {note ? <span className="ml-2 text-xs text-gray-400">({note})</span> : null}
      </div>
    </div>
  );
}

function AwardDetail({ holder, award, calc, dealPrice, closingDate, afrPct, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const detailId = useId();

  const typeLabel = award.type === 'option' ? 'OPT' : award.type === 'rsu' ? 'RSU' : 'RSA';
  const typeHue = award.type === 'option' ? 'bg-violet-50 text-violet-600' : 'bg-blue-50 text-blue-600';
  const shareLabel = award.type === 'option' ? 'options' : award.type === 'rsu' ? 'RSUs' : 'RSA shares';

  const showSteps = calc.earlyExit !== 'missing_inputs' && calc.earlyExit !== 'no_accel';

  return (
    <Card padding={false} className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={detailId}
        className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-50/80"
      >
        <div className="flex min-w-0 items-center space-x-3">
          <div
            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-xs font-semibold ${typeHue}`}
          >
            {typeLabel}
          </div>
          <div className="min-w-0 text-left">
            <div className="text-xs font-medium text-gray-900">
              {holder} — {(+award.totalShares || 0).toLocaleString()} {shareLabel}
            </div>
            <div className="truncate text-xs text-gray-500">
              Grant: {formatIsoYmdForDisplay(award.grantDate)} · {vestingSummary(award)}
              {award.type === 'option' ? ` · Strike: ${fmtCents(+award.exercisePrice || 0)}` : ''}
            </div>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center space-x-4">
          <div className="hidden text-right sm:block">
            <div className="text-xs text-gray-500">Full Value</div>
            <div className="text-xs font-medium tabular-nums text-gray-800">{fmt(calc.fullValue)}</div>
          </div>
          <div className="hidden text-right sm:block">
            <div className="text-xs text-gray-500">24(c) Value</div>
            <div className="text-xs font-medium tabular-nums text-emerald-600">{fmt(calc.parachuteValue)}</div>
          </div>
          <div className="hidden text-right md:block">
            <div className="text-xs text-gray-500">Reduction</div>
            <div className="text-xs font-medium tabular-nums text-violet-600">{pct(calc.reductionPct, 1)}</div>
          </div>
          {open ? (
            <ChevronDown size={16} className="flex-shrink-0 text-gray-400" aria-hidden />
          ) : (
            <ChevronRight size={16} className="flex-shrink-0 text-gray-400" aria-hidden />
          )}
        </div>
      </button>

      {open && (
        <div id={detailId} className="border-t border-gray-100 px-4 py-4 sm:px-5">
          <div
            className={`mb-4 flex items-start gap-2 rounded-lg px-3 py-2.5 text-xs leading-snug ${
              calc.uses24cDiscount
                ? 'border border-emerald-100 bg-emerald-50/60 text-emerald-800'
                : 'border border-amber-100 bg-amber-50/60 text-amber-800'
            }`}
          >
            {calc.uses24cDiscount ? (
              <CheckCircle size={14} className="mt-0.5 flex-shrink-0" aria-hidden />
            ) : (
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden />
            )}
            <span>{eligibleMessage(calc)}</span>
          </div>

          {showSteps && (
            <div className="space-y-4">
              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
                  <strong>Step 1</strong> — Unvested Shares Accelerating
                </div>
                <div className="space-y-1 rounded-lg bg-gray-50 p-3">
                  <StepRow label="Total shares granted" value={calc.totalShares.toLocaleString()} />
                  <StepRow label="Grant date" value={formatIsoYmdForDisplay(award.grantDate)} />
                  <StepRow label="Closing date (CIC)" value={formatIsoYmdForDisplay(closingDate)} />
                  <StepRow label="Months elapsed (approx.)" value={`${calc.monthsElapsed} months`} />
                  <StepRow
                    label="Vesting term"
                    value={`${calc.vestingDetails.months} months, ${calc.vestingDetails.cliffMonths}-month cliff`}
                  />
                  <StepRow
                    label="Shares vested before closing"
                    value={calc.sharesVestedPrior.toLocaleString()}
                    note="linear schedule through closing"
                  />
                  <StepRow
                    label="Unvested shares (pre-acceleration)"
                    value={calc.unvestedBeforeAccel.toLocaleString()}
                  />
                  <StepRow
                    label="Shares accelerating at closing"
                    value={calc.sharesVestingUponCIC.toLocaleString()}
                    highlight
                    note={`after ${award.accelerationType === 'partial' ? `partial (${award.accelerationPercentage}%)` : 'full'} acceleration`}
                  />
                </div>
              </div>

              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
                  <strong>Step 2</strong> — Full Value of Accelerated Payment
                </div>
                <div className="space-y-1 rounded-lg bg-gray-50 p-3">
                  <StepRow label="Deal price per share" value={fmtCents(dealPrice)} />
                  {award.type === 'option' && (
                    <>
                      <StepRow label="Exercise price" value={fmtCents(+award.exercisePrice || 0)} />
                      <StepRow
                        label="Spread per share"
                        value={fmtCents(calc.perShareValue)}
                        note={`${fmtCents(dealPrice)} − ${fmtCents(+award.exercisePrice || 0)}`}
                      />
                    </>
                  )}
                  {award.type === 'restricted_stock' && (
                    <>
                      <StepRow label="Purchase price" value={fmtCents(+award.purchasePrice || 0)} />
                      <StepRow
                        label="Value per share"
                        value={fmtCents(calc.perShareValue)}
                        note="deal price minus purchase"
                      />
                    </>
                  )}
                  {award.type !== 'option' && award.type !== 'restricted_stock' && (
                    <StepRow label="Value per share (RSU)" value={fmtCents(calc.perShareValue)} />
                  )}
                  <StepRow
                    label="Full value of accelerated shares"
                    value={fmt(calc.fullValue)}
                    highlight
                    note={`${calc.sharesVestingUponCIC.toLocaleString()} × ${fmtCents(calc.perShareValue)}`}
                  />
                </div>
                <p className="mt-1.5 text-xs italic leading-relaxed text-gray-500">
                  Without Q&A-24(c), this full amount is generally the starting point for the parachute payment from
                  equity acceleration.
                </p>
              </div>

              {calc.uses24cDiscount && (
                <>
                  <div>
                    <div className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
                      <strong>Step 3</strong> — Full Calendar Months of Acceleration (Q&A-24(c)(4))
                    </div>
                    <div className="space-y-1 rounded-lg bg-gray-50 p-3">
                      <StepRow label="Closing (accelerated vesting)" value={formatIsoYmdForDisplay(closingDate)} />
                      <StepRow
                        label="Original vesting end"
                        value={formatIsoYmdForDisplay(calc.vestingEndDateYmd)}
                        note={`grant + ${calc.vestingDetails.months} months`}
                      />
                      <StepRow
                        label="Full calendar months accelerated"
                        value={`${calc.monthsAccelerated} months`}
                        highlight
                        note="partial months ignored"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
                      <strong>Step 4</strong> — Lapse of Obligation (1% per Full Month)
                    </div>
                    <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3">
                      <div className="mb-2 text-xs text-gray-700">
                        <span className="font-semibold text-violet-700">Lapse component</span> = 1% × full value × full
                        months
                      </div>
                      <div className="rounded-md bg-white/60 px-3 py-2 text-center text-xs font-semibold tabular-nums text-violet-700">
                        1% × {fmt(calc.fullValue)} × {calc.monthsAccelerated} ={' '}
                        <span className="text-xs">{fmt(calc.lapseComponent)}</span>
                      </div>
                      <p className="mt-2 text-xs leading-relaxed text-gray-500">
                        Reflects the portion of value attributed to being released from the remaining service period.
                      </p>
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
                      <strong>Step 5</strong> — Acceleration of Payment (PV Interest)
                    </div>
                    <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-3">
                      <div className="mb-2 text-xs text-gray-700">
                        <span className="font-semibold text-blue-700">Acceleration component</span> = full value − (full
                        value ÷ PV factor)
                      </div>
                      <div className="mb-2 space-y-1">
                        <StepRow label="Short-term AFR" value={`${afrPct}%`} />
                        <StepRow label="120% of AFR (annual)" value={pct(calc.discountRateForPV)} />
                        <StepRow label="Semiannual rate" value={pct(calc.semiannualRate, 4)} />
                        <StepRow label="Years to original vest" value={calc.yearsAccelerated.toFixed(4)} />
                        <StepRow label="Semiannual periods" value={calc.periods.toFixed(4)} />
                        <StepRow label="PV factor" value={calc.pvFactor.toFixed(6)} />
                      </div>
                      <div className="rounded-md bg-white/60 px-3 py-2 text-center text-xs font-semibold tabular-nums text-blue-700">
                        {fmt(calc.fullValue)} − ({fmt(calc.fullValue)} ÷ {calc.pvFactor.toFixed(6)}) ={' '}
                        <span className="text-xs">{fmt(calc.accelerationComponent)}</span>
                      </div>
                      <p className="mt-2 text-xs leading-relaxed text-gray-500">
                        Present value uses 120% of the applicable federal rate, compounded semiannually (Q&A-24(b),
                        Q&A-32).
                      </p>
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
                      <strong>Step 6</strong> — Q&A-24(c) Parachute Amount
                    </div>
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
                      <div className="mb-2 text-xs text-gray-700">
                        <span className="font-semibold text-emerald-700">Parachute value</span> = min(full value, lapse
                        + acceleration)
                      </div>
                      <div className="mb-2 space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Lapse (step 4)</span>
                          <span className="font-medium tabular-nums text-violet-600">{fmt(calc.lapseComponent)}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Acceleration (step 5)</span>
                          <span className="font-medium tabular-nums text-blue-600">
                            {fmt(calc.accelerationComponent)}
                          </span>
                        </div>
                        <StepRow
                          label="Sum (before cap)"
                          value={fmt(calc.lapseComponent + calc.accelerationComponent)}
                        />
                        <StepRow label="Cap at full value" value={fmt(calc.fullValue)} />
                      </div>
                      <div className="rounded-md bg-white/60 px-3 py-2 text-center">
                        <div className="mb-1 text-xs text-gray-500">
                          min({fmt(calc.fullValue)}, {fmt(calc.lapseComponent)} + {fmt(calc.accelerationComponent)})
                        </div>
                        <div className="text-xs font-bold tabular-nums text-emerald-700">
                          {fmt(calc.parachuteValue)}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {calc.fullValue > 0 && calc.reductionFromFull > 0 && (
                <div>
                  <div className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
                    Summary — Compared to Full Spread
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Without 24(c)</div>
                        <div className="mt-1 text-xs font-bold tabular-nums text-gray-900">{fmt(calc.fullValue)}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wider text-gray-500">With 24(c)</div>
                        <div className="mt-1 text-xs font-bold tabular-nums text-emerald-600">
                          {fmt(calc.parachuteValue)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Reduction</div>
                        <div className="mt-1 text-xs font-bold tabular-nums text-violet-600">
                          {fmt(calc.reductionFromFull)}
                        </div>
                        <div className="mt-0.5 text-xs text-gray-500">{pct(calc.reductionPct, 1)}</div>
                      </div>
                    </div>
                    <div className="relative mt-3 h-4 overflow-hidden rounded-full bg-gray-200">
                      <div
                        className="absolute inset-y-0 left-0 rounded-l-full bg-emerald-500"
                        style={{
                          width: `${Math.min(100, (calc.parachuteValue / calc.fullValue) * 100)}%`,
                        }}
                      />
                      <div
                        className="absolute inset-y-0 rounded-r-full bg-violet-300"
                        style={{
                          left: `${Math.min(100, (calc.parachuteValue / calc.fullValue) * 100)}%`,
                          width: `${Math.min(100, (calc.reductionFromFull / calc.fullValue) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function ReadOnlyField({ label, value, note }) {
  return (
    <div>
      <span className="mb-2 block text-xs font-medium text-gray-700">{label}</span>
      <div className="flex min-h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-xs tabular-nums text-gray-900">
        {value}
      </div>
      {note ? <p className="mt-1 text-xs text-gray-500">{note}</p> : null}
    </div>
  );
}

export function EquityAwardsQa24c({ formData }) {
  const rowsFromForm = useMemo(() => flattenAwardsFromForm(formData), [formData?.individuals, formData?.equityData]);
  const hasAwards = rowsFromForm.length > 0;

  const { dealPrice, closingDate, afrPct, discountRateForPV } = useMemo(() => {
    const dp = +formData?.dealPrice;
    const dealOk = Number.isFinite(dp) && dp > 0;
    const cd = String(formData?.closingDate || '')
      .trim()
      .slice(0, 10);
    const af = +formData?.afr;
    const afrOk = Number.isFinite(af) && af >= 0;
    const afrSafe = afrOk ? af : 5;
    return {
      dealPrice: dealOk ? dp : 0,
      closingDate: cd,
      afrPct: afrSafe,
      discountRateForPV: 1.2 * (afrSafe / 100),
    };
  }, [formData?.dealPrice, formData?.closingDate, formData?.afr]);

  const allCalcs = useMemo(
    () =>
      rowsFromForm.map(({ award }) =>
        calculateEquityAwardQa24cBreakdown({
          award,
          closingDate,
          dealPrice,
          discountRateForPV,
        })
      ),
    [rowsFromForm, closingDate, dealPrice, discountRateForPV]
  );

  const totals = useMemo(() => {
    let totalFull = 0;
    let totalParachute = 0;
    for (const c of allCalcs) {
      totalFull += c.fullValue;
      totalParachute += c.parachuteValue;
    }
    return {
      totalFull,
      totalParachute,
      totalReduction: totalFull - totalParachute,
    };
  }, [allCalcs]);

  return (
    <div className="space-y-4">
      {!hasAwards ? (
        <InfoBanner variant="warning" showIcon>
          <span>
            There are <strong>no equity awards</strong> in this analysis yet. Add awards on the{' '}
            <strong>Equity Awards</strong> step; this page will list each award and the Q&A-24(c) calculation detail.
          </span>
        </InfoBanner>
      ) : null}

      <InfoBanner variant="info">
        <strong>Methodology:</strong> For eligible time-based awards granted more than 12 months before closing,
        parachute value from vesting acceleration is the lesser of the full payment value and the sum of (1){' '}
        <strong>1% of that value per full calendar month</strong> of accelerated vesting and (2) the{' '}
        <strong>present-value “acceleration of payment” component</strong> using 120% AFR compounded semiannually. This
        page is read-only and mirrors the Transaction step, AFR, and Equity Awards used in 280G results.
      </InfoBanner>

      <Card>
        <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
          <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Transaction Parameters</div>
          <p className="text-xs text-gray-500">
            Values come from your Transaction step; edit them there to update this view.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <ReadOnlyField label="Deal Price / Share" value={fmtCents(dealPrice)} />
          <ReadOnlyField label="Closing Date (CIC)" value={closingDate ? formatIsoYmdForDisplay(closingDate) : '—'} />
          <ReadOnlyField
            label="Short-Term AFR (%)"
            value={`${afrPct}%`}
            note={`120% AFR: ${pct(discountRateForPV)}, semiannual: ${pct(discountRateForPV / 2, 4)}`}
          />
        </div>
      </Card>

      {hasAwards ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Card padding={false} className="p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Total Full Value</p>
            <p className="mt-1 text-xs font-semibold tabular-nums text-gray-900">{fmt(totals.totalFull)}</p>
            <p className="mt-0.5 text-xs text-gray-500">Before any Q&A-24(c) adjustment</p>
          </Card>
          <Card padding={false} className="p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Total 24(c) Value</p>
            <p className="mt-1 text-xs font-semibold tabular-nums text-emerald-600">{fmt(totals.totalParachute)}</p>
            <p className="mt-0.5 text-xs text-gray-500">Parachute amount used in the model</p>
          </Card>
          <Card padding={false} className="p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Total Reduction</p>
            <p className="mt-1 text-xs font-semibold tabular-nums text-violet-600">{fmt(totals.totalReduction)}</p>
            <p className="mt-0.5 text-xs text-gray-500">
              {totals.totalFull > 0 ? pct(totals.totalReduction / totals.totalFull, 1) : '0%'} vs. full spread
            </p>
          </Card>
        </div>
      ) : null}

      <div className="space-y-3">
        {hasAwards ? (
          <>
            <h3 className="text-xs font-medium text-gray-900">Your Awards — Step-by-Step</h3>
            {rowsFromForm.map(({ holderId, holder, award }, i) => (
              <AwardDetail
                key={`${holderId}-${award.id ?? i}`}
                holder={holder}
                award={award}
                calc={allCalcs[i]}
                dealPrice={dealPrice}
                closingDate={closingDate}
                afrPct={afrPct}
                defaultOpen={i === 0}
              />
            ))}
          </>
        ) : (
          <Card padding={false} className="px-4 py-8 sm:px-5">
            <div className="flex items-center gap-3">
              <Award size={28} className="shrink-0 text-gray-300" aria-hidden />
              <p className="text-left text-xs text-gray-500">
                No equity awards to show. Use <span className="font-medium text-gray-700">Equity Awards</span> in this
                workflow to add awards first.
              </p>
            </div>
          </Card>
        )}
      </div>

      <div className="space-y-1.5 text-xs leading-relaxed text-gray-500">
        <p className="font-medium text-gray-700">Regulatory references (abbreviated)</p>
        <p>Treas. Reg. 1.280G-1, Q&A-24(c) — lapse of obligation to perform services (1% per full month).</p>
        <p>Treas. Reg. 1.280G-1, Q&A-24(c)(4) — full calendar months; partial months disregarded.</p>
        <p>Treas. Reg. 1.280G-1, Q&A-24(b) and Q&A-32 — PV of acceleration using 120% AFR, semiannual compounding.</p>
        <p>
          Treas. Reg. 1.280G-1, Q&A-24(a)(2) — Q&A-24(c) amounts are not further reduced as reasonable compensation.
        </p>
      </div>
    </div>
  );
}
